// Phase 8.7 — LiftedAtTheMoment Collections Admin
// Edit titles, descriptions, hide/show, sort order.

import React, { useEffect, useState, useCallback } from "react";
import { BookOpen, Save, Loader2, EyeOff, Eye } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import AdminFeatureShell from "@/components/admin/AdminFeatureShell";

export default function AdminCollections() {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [savedSlug, setSavedSlug] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/admin/storefront/collections");
      setCollections(data.collections || []);
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pick = (c) => {
    setSelected(c.slug);
    setDraft({
      title: c.title || "",
      description: c.description || "",
      image: c.image || "",
      sort_order: c.sort_order || 0,
      hidden: !!c.hidden,
    });
  };

  const save = async () => {
    setSaving(true); setErr(""); setSavedSlug(null);
    try {
      await api.patch(`/admin/storefront/collections/${selected}`, draft);
      setSavedSlug(selected);
      await load();
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setSaving(false); }
  };

  return (
    <AdminFeatureShell
      breadcrumb="LiftedAtTheMoment / Catalog"
      title="Collections"
      description="Manage storefront collections — lifted-gear, lifted-accessories, themed drops. Counts reflect the live catalog."
      status={`${collections.length} collections`}
      testId="admin-collections"
    >
      {err && <div className="glass-panel border border-rose-500/30 p-3 text-rose-200 text-sm">{err}</div>}

      <div className="grid lg:grid-cols-[1fr_420px] gap-4 items-start">
        <div className="space-y-2" data-testid="collections-list">
          {loading && <div className="text-sm text-lifted-dim">Loading…</div>}
          {!loading && collections.length === 0 && (
            <div className="glass-panel p-6 text-sm text-lifted-dim text-center">
              No collections found. Import from Shopify or seed manually.
            </div>
          )}
          {collections.map((c) => (
            <button key={c.slug} onClick={() => pick(c)}
                    className={`w-full glass-panel p-3 text-left flex items-center gap-3 transition ${
                      selected === c.slug ? "border-lifted-green/40" : "hover:border-white/20"
                    }`}
                    data-testid={`collection-row-${c.slug}`}>
              <div className="h-12 w-12 rounded-md bg-black/40 overflow-hidden shrink-0 grid place-items-center">
                {c.image ? (
                  <img src={c.image} alt="" className="h-full w-full object-cover" />
                ) : (
                  <BookOpen className="h-4 w-4 text-lifted-dim" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-lifted-text truncate">{c.title}</div>
                <div className="text-[0.65rem] text-lifted-dim flex items-center gap-2 mt-0.5">
                  <span className="font-mono">{c.slug}</span>
                  <span>·</span>
                  <span>{c.product_count} products</span>
                  {c.hidden && (
                    <span className="text-rose-300 inline-flex items-center gap-1"><EyeOff className="h-3 w-3" /> Hidden</span>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="lg:sticky lg:top-4">
          {selected ? (
            <div className="glass-panel p-5 space-y-4" data-testid="collection-editor">
              <div className="font-bebas text-[0.65rem] tracking-[0.22em] text-lifted-green">EDIT COLLECTION</div>
              <div className="text-sm font-mono text-lifted-dim">{selected}</div>

              <Field label="Title" value={draft.title} onChange={(v) => setDraft((d) => ({ ...d, title: v }))} testid="collection-title" />
              <Field label="Description" value={draft.description} onChange={(v) => setDraft((d) => ({ ...d, description: v }))} multiline testid="collection-description" />
              <Field label="Hero image URL" value={draft.image} onChange={(v) => setDraft((d) => ({ ...d, image: v }))} testid="collection-image" />
              <div className="grid grid-cols-2 gap-2">
                <Field label="Sort order" value={draft.sort_order}
                       onChange={(v) => setDraft((d) => ({ ...d, sort_order: parseInt(v) || 0 }))}
                       type="number" testid="collection-sort" />
                <div>
                  <Label>Visibility</Label>
                  <button type="button" onClick={() => setDraft((d) => ({ ...d, hidden: !d.hidden }))}
                          className={`w-full border rounded-md px-3 py-2 text-sm inline-flex items-center gap-2 ${
                            draft.hidden ? "bg-rose-500/15 text-rose-200 border-rose-400/40" : "border-lifted-green/40 text-lifted-green"
                          }`}
                          data-testid="collection-visibility">
                    {draft.hidden ? <><EyeOff className="h-3 w-3" /> Hidden</> : <><Eye className="h-3 w-3" /> Visible</>}
                  </button>
                </div>
              </div>

              <button onClick={save} disabled={saving}
                      className="btn-lifted !py-2 !px-4 text-xs inline-flex items-center gap-2 disabled:opacity-50"
                      data-testid="collection-save">
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                Save collection
              </button>
              {savedSlug === selected && (
                <div className="text-xs text-lifted-green" data-testid="collection-saved">Saved.</div>
              )}
            </div>
          ) : (
            <div className="glass-panel p-6 text-center text-xs text-lifted-dim">
              <BookOpen className="h-6 w-6 mx-auto mb-2 text-lifted-green" />
              Select a collection to edit.
            </div>
          )}
        </div>
      </div>
    </AdminFeatureShell>
  );
}

function Label({ children }) {
  return <div className="font-bebas text-[0.6rem] tracking-[0.22em] text-lifted-dim mb-1.5">{children}</div>;
}

function Field({ label, value, onChange, multiline, type = "text", testid }) {
  return (
    <div>
      <Label>{label}</Label>
      {multiline ? (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3}
          className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:border-lifted-green/40 focus:outline-none"
          data-testid={testid} />
      ) : (
        <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
          className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:border-lifted-green/40 focus:outline-none"
          data-testid={testid} />
      )}
    </div>
  );
}
